import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, Label, PasswordWithConfirm } from '@/shared/ui'
import { Modal } from '@/shared/ui/Modal'
import { Mail, Share2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'

export default function AffiliateRegisterPage() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const token = (params.get('token') || '').trim()

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [blockedModal, setBlockedModal] = React.useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) { setError('Link de afiliado inválido'); return }
    const em = (email || '').trim().toLowerCase()
    if (!/.+@.+\..+/.test(em)) { setError('Informe um e-mail válido'); return }
    if ((password || '').length < 8) { setError('A senha deve ter pelo menos 8 caracteres'); return }
    if (password !== confirm) { setError('As senhas não conferem'); return }
    try {
      setSubmitting(true)
      const { data, error, /* @ts-ignore */ response } = await (supabase.functions as any).invoke('affiliate-register-activate', { body: { token, email: em, password } as any })

      if (error) {
        try {
          const ctx: Response | undefined = (error as any)?.context || response
          if (ctx) {
            const ct = ctx.headers.get('content-type') || ''
            if (ct.includes('application/json')) {
              const j = await ctx.json().catch(() => null as any)
              const code = j?.error as string | undefined
              const msg = (j?.message as string | undefined) || ''
              if (code === 'email_not_authorized' || code === 'invalid_or_used_token') { setBlockedModal(true); return }
              if (code === 'internal_error') { setError('Falha no cadastro de afiliado. Tente novamente.'); console.warn('affiliate-register-activate internal_error:', msg); return }
              if (code) { setError(code); return }
            } else {
              const t = await ctx.text().catch(() => '')
              if (t) { setError(t); return }
            }
          }
        } catch {}
        setError('Falha no cadastro do afiliado')
        return
      }

      const resp = (Array.isArray(data) ? (data as any)[0] : (data as any)) || {}
      if (!resp?.ok) {
        if (resp?.error === 'email_not_authorized' || resp?.error === 'invalid_or_used_token') { setBlockedModal(true); return }
        if (resp?.error === 'internal_error') setError('Falha no cadastro de afiliado. Tente novamente.')
        else setError(String(resp?.error || 'Falha no cadastro do afiliado'))
        return
      }

      try { await supabase.auth.signInWithPassword({ email: em, password }) } catch {}
      nav('/dashboard-affiliate', { replace: true })
    } catch (e: any) {
      const msg = e?.message || 'Falha no cadastro do afiliado'
      if (msg.toLowerCase().includes('not_authorized') || msg.toLowerCase().includes('invalid_or_used_token')) { setBlockedModal(true) }
      else setError(msg)
    } finally { setSubmitting(false) }
  }

  return (
    <div className="mx-auto w-full max-w-[480px] px-4 py-10">
      <div className="mb-6 flex items-center gap-2">
        <Share2 className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Cadastro de Afiliado</h1>
      </div>
      <form className="space-y-4" onSubmit={onSubmit}>
        {error && (<div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>)}
        <div className="space-y-1.5">
          <Label htmlFor="email" className="after:ml-1 after:text-destructive after:content-['*']">E-mail</Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
            <Input id="email" type="email" value={email} onChange={e=> setEmail(e.target.value)} placeholder="nome@empresa.com" className="pl-9" required />
          </div>
        </div>
        <PasswordWithConfirm
          columns={1}
          password={password}
          confirm={confirm}
          onChange={(p)=> { if (typeof p.password === 'string') setPassword(p.password); if (typeof p.confirm === 'string') setConfirm(p.confirm) }}
          required
          labels={{ password: 'Senha', confirm: 'Confirmar senha' }}
          placeholders={{ password: 'Crie uma senha', confirm: 'Repita a senha' }}
        />
        <div className="pt-2 text-right">
          <Button type="submit" disabled={submitting}>{submitting ? 'Enviando...' : 'Cadastrar'}</Button>
        </div>
      </form>

      <Modal open={blockedModal} onOpenChange={setBlockedModal}>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Cadastro não permitido</h2>
          <p className="text-sm text-foreground/70">Este e-mail não está autorizado para o acesso de afiliado. Verifique com o administrador.</p>
          <div className="flex justify-end">
            <Button onClick={()=> setBlockedModal(false)}>Fechar</Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

